import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Hooks for the driver Cylinder Audit PWA (/audit) and the Tank Rental
 * Audit document rendered on CR-ADJ invoices.
 *
 * All data flows through the driver-gated audit-* Netlify functions —
 * drivers hold no customer membership, so nothing here reads RLS views
 * directly (except useIsDriver, which reads the caller's own
 * customer_users row).
 */

export async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('not signed in');
  return { Authorization: `Bearer ${token}` };
}

/** Whether the signed-in user may use the driver audit app. */
export function useIsDriver(): { isDriver: boolean; loading: boolean } {
  const { data, isLoading } = useQuery<boolean>({
    queryKey: ['is-driver'],
    queryFn: async () => {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) return false;
      const { data: row, error } = await supabase
        .schema('orders')
        .from('customer_users')
        .select('is_driver, is_superadmin')
        .eq('id', userId)
        .maybeSingle();
      if (error) return false;
      return row?.is_driver === true || row?.is_superadmin === true;
    },
    staleTime: 5 * 60 * 1000,
  });
  return { isDriver: data === true, loading: isLoading };
}

export interface AuditCustomer {
  /** Portal customer row; null for a rental account that only exists in QuickBooks. */
  id: string | null;
  name: string;
  qbo_customer_id: number;
  address: string | null;
  /**
   * An open row in orders.cylinder_audit_alerts — the books and the yard have
   * already disagreed here and nobody has counted it yet. The server sorts
   * these to the top of the picker.
   */
  needs_audit?: boolean;
  audit_labels?: string[];
  audit_since?: string | null;
}

export function useAuditCustomers(q: string) {
  return useQuery<AuditCustomer[]>({
    queryKey: ['audit-customers', q],
    queryFn: async () => {
      const headers = await authHeader();
      const res = await fetch(
        `/.netlify/functions/audit-list-customers?q=${encodeURIComponent(q)}`,
        { headers },
      );
      const body = (await res.json()) as { ok: boolean; customers?: AuditCustomer[]; error?: string };
      if (!res.ok || !body.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      return body.customers ?? [];
    },
    staleTime: 60 * 1000,
  });
}

export interface AuditBucket {
  label: string;
  system_count: number;
  btrf_doc_number: string | null;
  btrf_date: string | null;
  tracked: boolean;
}

export interface AuditCountsResponse {
  /** `id` is the portal customer row, null for an account that only exists in QuickBooks. */
  customer: { id: string | null; name: string; qbo_customer_id: number };
  buckets: AuditBucket[];
  /** The signed-in user is a manager/admin and may submit a count change without a photo. */
  can_skip_photo?: boolean;
}

/**
 * Who is being audited — a portal customer (the driver picker) OR a QuickBooks
 * customer with no portal row (the tank-rental screen). Exactly one is set.
 */
export type AuditSubjectKey = { customerId: string; qboId?: undefined } | { qboId: string; customerId?: undefined };

export function auditSubjectQuery(k: AuditSubjectKey): string {
  return k.customerId ? `customer_id=${encodeURIComponent(k.customerId)}` : `qbo_customer_id=${encodeURIComponent(k.qboId ?? '')}`;
}

export function useAuditCounts(key: AuditSubjectKey | undefined) {
  return useQuery<AuditCountsResponse>({
    queryKey: ['audit-counts', key?.customerId ?? null, key?.qboId ?? null],
    enabled: !!key,
    queryFn: async () => {
      const headers = await authHeader();
      const res = await fetch(
        `/.netlify/functions/audit-cylinder-counts?${auditSubjectQuery(key!)}`,
        { headers },
      );
      const body = (await res.json()) as (AuditCountsResponse & { ok: boolean; error?: string });
      if (!res.ok || !body.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      return { customer: body.customer, buckets: body.buckets, can_skip_photo: body.can_skip_photo === true };
    },
    staleTime: 30 * 1000,
  });
}

export interface SubmitAuditInput {
  /** Portal customer uuid — or omit it and pass qbo_customer_id for an account with no portal row. */
  customer_id?: string;
  qbo_customer_id?: string;
  counts: Array<{ label: string; counted: number }>;
  photos: Array<{ data_base64: string; content_type: string }>;
  signature_data_url: string;
  signed_name: string;
  /** Optional on-site manager countersignature. */
  manager_name?: string;
  manager_signature_data_url?: string;
  notes?: string;
}

export interface SubmitAuditResult {
  ok: boolean;
  audit_id: string;
  doc_number: string;
  status: 'balanced' | 'adjusted' | 'invoice_failed' | 'pending_approval';
  has_adjustment: boolean;
  qbo_invoice_id: string | null;
  invoice_error: string | null;
  note: string;
  counts: Array<{ label: string; system_count: number; counted: number; delta: number }>;
}

export function useSubmitAudit() {
  return useMutation<SubmitAuditResult, Error, SubmitAuditInput>({
    mutationFn: async (input) => {
      const headers = await authHeader();
      const res = await fetch('/.netlify/functions/audit-submit', {
        method: 'POST',
        headers: { ...headers, 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      const body = (await res.json().catch(() => ({}))) as SubmitAuditResult & { error?: string };
      if (!res.ok || !body.ok) {
        throw new Error(body.error ?? `Submit failed (HTTP ${res.status})`);
      }
      return body;
    },
  });
}

export interface AuditCountEntry {
  label: string;
  system_count: number;
  counted: number;
  delta: number;
  item_name: string | null;
}

export interface AuditDocument {
  id: string;
  doc_number: string;
  customer_id: string | null;
  qbo_customer_id?: string | null;
  driver_name: string | null;
  counts: AuditCountEntry[];
  /** Manager amendments applied at approval; null when approved as-signed. */
  final_counts: AuditCountEntry[] | null;
  has_adjustment: boolean;
  qbo_invoice_id: string | null;
  signed_name: string;
  signed_at: string;
  manager_name: string | null;
  manager_signature_url: string | null;
  approved_by_email: string | null;
  approved_at: string | null;
  approval_notes: string | null;
  notes: string | null;
  /** Set when a manager/admin filed a count change with no photo. */
  photo_waived_by?: string | null;
  status: string;
  /** The last QuickBooks refusal for the CR-ADJ invoice; null once it books. */
  invoice_error?: string | null;
  created_at: string;
  /** Missing-tank replacement charge (CR-LOST-<n>), when one was booked. */
  charge_qbo_invoice_id?: string | null;
  charge_doc_number?: string | null;
  charge_tanks?: number | null;
  charge_rate?: number | null;
  charge_amount?: number | null;
  charged_at?: string | null;
  charged_by_email?: string | null;
  signature_url: string | null;
  photos: Array<{ url: string; path: string }>;
}

/**
 * Load one Tank Rental Audit by audit id or by the CR-ADJ invoice's
 * qbo_invoice_id. Returns null (not an error) when no audit exists for the
 * given key — the invoice page probes with every CR-ADJ invoice id.
 */
export function useAuditDocument(params: { id?: string | null; invoiceId?: string | null }) {
  const key = params.id ? `id=${params.id}` : params.invoiceId ? `invoice_id=${params.invoiceId}` : null;
  return useQuery<AuditDocument | null>({
    queryKey: ['audit-document', key],
    enabled: !!key,
    queryFn: async () => {
      const headers = await authHeader();
      const res = await fetch(`/.netlify/functions/audit-get?${key}`, { headers });
      if (res.status === 404) return null;
      const body = (await res.json()) as { ok: boolean; audit?: AuditDocument; error?: string };
      if (!res.ok || !body.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      return body.audit ?? null;
    },
    // Signed URLs expire in 10 minutes; refetch a little before that.
    staleTime: 8 * 60 * 1000,
  });
}
